




















































































































import { Component, Watch } from 'vue-property-decorator';
import { WorkerWizardModule } from '@/store/modules/workerWizard';
import DuplicateWorkerMatch from '@/models/Workers/DuplicateWorkerMatch';
import WorkerStatus from '@/components/Workers/Display/WorkerStatus.vue';
import EntityType from '@/models/Shared/EntityType';
import ComponentLocation from '@/models/Shared/ComponentLocation';
import StatusDisplayLocation from '@/models/Shared/StatusDisplayLocation';
import GiganautVue from '@/components/Shared/GiganautVue';
import { AppModule } from '@/store/modules/app';

@Component({
  name: 'WorkerWizardDuplicateCheck',
  components: { WorkerStatus }
})
export default class WorkerWizardDuplicateCheck extends GiganautVue {
  hasChecked = false;

  // Expose enums to template
  EntityType = EntityType;
  ComponentLocation = ComponentLocation;
  StatusDisplayLocation = StatusDisplayLocation;

  headers = [
    { text: '', value: 'selected', sortable: false, width: '50px' },
    { text: '', value: 'avatar', sortable: false, width: '60px' },
    { text: 'Ref', value: 'sequenceNumber', width: '70px' },
    { text: 'Name', value: 'fullName' },
    { text: 'Mobile', value: 'mobilePhoneNumber' },
    { text: 'Email', value: 'emailAddress' },
    { text: 'Work Types', value: 'workTypes', sortable: false },
    { text: 'Status', value: 'status', sortable: false },
    { text: 'Name Match', value: 'nameMatch', sortable: false, align: 'center', width: '100px' },
    { text: 'Email Match', value: 'emailMatch', sortable: false, align: 'center', width: '100px' },
    { text: '', value: 'actions', sortable: false, align: 'right', width: '50px' },
  ];

  // Watch the current step - run check when step 5 (Match) becomes active
  get currentStep(): number {
    return WorkerWizardModule.newWorker.currentStep;
  }

  @Watch('currentStep', { immediate: true })
  onStepChanged(newStep: number) {
    // Step 5 is the Match step - run check when we enter it
    if (newStep === 5 && !this.hasChecked) {
      this.runDuplicateCheck();
    }
  }

  mounted() {
    // Also check on mount in case we're already on step 5
    if (this.currentStep === 5) {
      this.runDuplicateCheck();
    }
  }

  async runDuplicateCheck() {
    if (this.hasChecked) return;
    this.hasChecked = true;
    await WorkerWizardModule.checkForDuplicates();
  }

  get loading(): boolean {
    return WorkerWizardModule.duplicateCheckLoading;
  }

  get matches(): DuplicateWorkerMatch[] {
    return WorkerWizardModule.duplicateMatches;
  }

  get hasMatches(): boolean {
    return this.matches.length > 0;
  }

  get selectedMatch(): DuplicateWorkerMatch | null {
    return WorkerWizardModule.selectedDuplicateMatch;
  }

  get newWorker() {
    return WorkerWizardModule.newWorker.worker;
  }

  get searchCriteria() {
    return {
      firstName: this.newWorker.firstName || '',
      lastName: this.newWorker.lastName || '',
      email: this.newWorker.emailAddress || ''
    };
  }

  get hasAnyEmailMatch(): boolean {
    return this.matches.some(m => m.emailMatch);
  }

  // Avatar URL helper
  getWorkerAvatarUrl(worker: any): string {
    if (!worker.avatar) return '';
    return AppModule.currentAgencyContext.storageSharedBaseUrl + "/avatars/3/" + worker.id + "/resized-100/" + worker.avatar;
  }

  // Methods
  canSelectMatch(match: DuplicateWorkerMatch): boolean {
    // If there's an email match present, only allow selecting workers with email match
    if (this.hasAnyEmailMatch) {
      return match.emailMatch;
    }
    return true;
  }

  isSelected(match: DuplicateWorkerMatch): boolean {
    return this.selectedMatch?.worker.id === match.worker.id;
  }

  getRowClass(match: DuplicateWorkerMatch): string {
    const classes: string[] = [];
    if (this.isSelected(match)) {
      classes.push('selected-row');
    }
    if (!this.canSelectMatch(match)) {
      classes.push('disabled-row');
    } else {
      classes.push('cursor-pointer');
    }
    return classes.join(' ');
  }

  getPersonName(worker: any): string {
    const firstName = worker.person ? worker.person.firstName : '';
    const lastName = worker.person ? worker.person.lastName : '';
    return `${firstName} ${lastName}`.trim() || 'Unknown';
  }

  toggleSelection(match: DuplicateWorkerMatch) {
    if (this.isSelected(match)) {
      WorkerWizardModule.clearDuplicateSelection();
    } else {
      WorkerWizardModule.selectDuplicateMatch(match);
    }
  }

  clearSelection() {
    WorkerWizardModule.clearDuplicateSelection();
  }

  // Called by parent to validate this step
  async validateForm(): Promise<boolean> {
    // If there's an email match, a selection is required
    if (this.hasAnyEmailMatch && !this.selectedMatch) {
      return true; // Return true to indicate validation failed (has errors)
    }
    return false; // No errors
  }

  // Check if user can proceed to next step
  canProceed(): boolean {
    // If there's an email match, must have a selection
    if (this.hasAnyEmailMatch) {
      return !!this.selectedMatch;
    }
    // Otherwise can always proceed (either with selection or as new)
    return true;
  }

  // Reset check when re-entering the step (called by parent when going back)
  resetCheck() {
    this.hasChecked = false;
    WorkerWizardModule.resetDuplicateCheck();
  }
}
